import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Upload, FileText, CheckCircle, AlertTriangle, Loader2, RefreshCw } from 'lucide-react';
import { useToast } from '@/components/use-toast';
import { UploadFile, ExtractDataFromUploadedFile } from '@/api/integrations';

// Import all entities for upload
import { AccountFinal } from '@/api/entities';
import { PersonFinal } from '@/api/entities';
import { Procurement } from '@/api/entities';
import { PersonaActivity } from '@/api/entities';
import { MomentumScore } from '@/api/entities';
import { Deal } from '@/api/entities';
import { Playbook } from '@/api/entities';

const entityOptions = [
    { name: "AccountFinal", entity: AccountFinal, label: "Accounts" },
    { name: "PersonFinal", entity: PersonFinal, label: "People" },
    { name: "Procurement", entity: Procurement, label: "Procurement" },
    { name: "PersonaActivity", entity: PersonaActivity, label: "Persona Activity" },
    { name: "MomentumScore", entity: MomentumScore, label: "Momentum Scores" },
    { name: "Deal", entity: Deal, label: "Deals" },
    { name: "Playbook", entity: Playbook, label: "Playbooks" },
];

export default function DataUploader({ onUploadComplete }) {
    const [selectedEntity, setSelectedEntity] = useState('');
    const [file, setFile] = useState(null);
    const [uploadStep, setUploadStep] = useState('select'); // select, upload, preview, confirm, processing, complete
    const [extractedData, setExtractedData] = useState([]);
    const [fieldMapping, setFieldMapping] = useState({});
    const [conflicts, setConflicts] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [uploadResults, setUploadResults] = useState({ success: 0, errors: 0, conflicts: 0 });
    const { toast } = useToast();

    const handleEntitySelect = (entityName) => {
        setSelectedEntity(entityName);
        setUploadStep('upload');
    };

    const handleFileSelect = (event) => {
        const selectedFile = event.target.files[0];
        if (selectedFile && selectedFile.type === 'text/csv') {
            setFile(selectedFile);
        } else {
            toast({ title: "Invalid file", description: "Please select a CSV file.", variant: "destructive" });
        }
    };

    const handleFileUpload = async () => {
        if (!file || !selectedEntity) return;
        
        setIsLoading(true);
        try {
            // Upload file first
            const { file_url } = await UploadFile({ file });
            
            // Get entity schema to validate against
            const entityConfig = entityOptions.find(e => e.name === selectedEntity);
            const schema = await entityConfig.entity.schema();
            
            // Extract data from CSV - fix the schema structure
            const extractResult = await ExtractDataFromUploadedFile({
                file_url,
                json_schema: {
                    type: "object",
                    properties: schema.properties || schema, // Use schema.properties if available, otherwise use schema directly
                    additionalProperties: true // Allow extra fields
                }
            });

            if (extractResult.status === 'error') {
                throw new Error(extractResult.details);
            }

            const data = Array.isArray(extractResult.output) ? extractResult.output : [extractResult.output];
            setExtractedData(data);
            
            // Analyze field mapping and conflicts
            await analyzeDataMapping(data, schema, entityConfig.entity);
            
            setUploadStep('preview');
        } catch (error) {
            console.error('Upload error:', error);
            toast({ 
                title: "Upload failed", 
                description: error.message || "Failed to process the CSV file.", 
                variant: "destructive" 
            });
        }
        setIsLoading(false);
    };

    const analyzeDataMapping = async (data, schema, entity) => {
        // Get the actual properties from the schema
        const schemaProperties = schema.properties || schema;
        const schemaFields = Object.keys(schemaProperties);
        const dataFields = data.length > 0 ? Object.keys(data[0]) : [];
        
        // Create field mapping
        const mapping = {};
        const unmappedFields = [];
        
        dataFields.forEach(dataField => {
            const exactMatch = schemaFields.find(schemaField => 
                schemaField.toLowerCase() === dataField.toLowerCase()
            );
            
            if (exactMatch) {
                mapping[dataField] = exactMatch;
            } else {
                // Try fuzzy matching
                const fuzzyMatch = schemaFields.find(schemaField => 
                    schemaField.toLowerCase().includes(dataField.toLowerCase()) ||
                    dataField.toLowerCase().includes(schemaField.toLowerCase())
                );
                
                if (fuzzyMatch) {
                    mapping[dataField] = fuzzyMatch;
                } else {
                    unmappedFields.push(dataField);
                }
            }
        });
        
        setFieldMapping(mapping);
        
        // Check for potential conflicts by looking for existing records
        const conflictChecks = [];
        for (const record of data.slice(0, 10)) { // Check first 10 records
            try {
                let existingRecord = null;
                
                // Try to find existing record based on key fields
                if (selectedEntity === 'AccountFinal' && record.account_name) {
                    const existing = await entity.filter({ account_name: record.account_name });
                    existingRecord = existing[0];
                } else if (selectedEntity === 'PersonFinal' && record.first_name && record.last_name) {
                    const existing = await entity.filter({ 
                        first_name: record.first_name, 
                        last_name: record.last_name 
                    });
                    existingRecord = existing[0];
                }
                
                if (existingRecord) {
                    conflictChecks.push({
                        newRecord: record,
                        existingRecord,
                        action: 'update' // Default to update
                    });
                }
            } catch (error) {
                // Ignore errors in conflict checking
                console.warn('Conflict check error:', error);
            }
        }
        
        setConflicts(conflictChecks);
        
        if (unmappedFields.length > 0) {
            toast({
                title: "Field mapping issues",
                description: `Some fields couldn't be mapped: ${unmappedFields.join(', ')}`,
                variant: "destructive"
            });
        }
    };

    const handleDataUpload = async () => {
        setIsLoading(true);
        setUploadStep('processing');
        
        const entityConfig = entityOptions.find(e => e.name === selectedEntity);
        const results = { success: 0, errors: 0, conflicts: 0 };
        
        try {
            for (const record of extractedData) {
                try {
                    // Map fields according to field mapping
                    const mappedRecord = {};
                    Object.entries(record).forEach(([key, value]) => {
                        const mappedKey = fieldMapping[key] || key;
                        mappedRecord[mappedKey] = value;
                    });
                    
                    // Check if record exists and handle conflicts
                    let existingRecord = null;
                    if (selectedEntity === 'AccountFinal' && mappedRecord.account_name) {
                        const existing = await entityConfig.entity.filter({ account_name: mappedRecord.account_name });
                        existingRecord = existing[0];
                    } else if (selectedEntity === 'PersonFinal' && mappedRecord.first_name && mappedRecord.last_name) {
                        const existing = await entityConfig.entity.filter({ 
                            first_name: mappedRecord.first_name, 
                            last_name: mappedRecord.last_name 
                        });
                        existingRecord = existing[0];
                    }
                    
                    if (existingRecord) {
                        // Update existing record
                        await entityConfig.entity.update(existingRecord.id, mappedRecord);
                        results.conflicts++;
                    } else {
                        // Create new record
                        await entityConfig.entity.create(mappedRecord);
                        results.success++;
                    }
                } catch (error) {
                    console.error('Record upload error:', error);
                    results.errors++;
                }
            }
            
            setUploadResults(results);
            setUploadStep('complete');
            
            toast({
                title: "Upload complete",
                description: `${results.success} new records created, ${results.conflicts} updated, ${results.errors} errors.`
            });
            
            // Notify parent component to refresh data
            if (onUploadComplete) {
                onUploadComplete();
            }
            
        } catch (error) {
            console.error('Bulk upload error:', error);
            toast({
                title: "Upload failed",
                description: error.message || "Failed to upload data.",
                variant: "destructive"
            });
            setUploadStep('preview');
        }
        
        setIsLoading(false);
    };

    const resetUploader = () => {
        setSelectedEntity('');
        setFile(null);
        setUploadStep('select');
        setExtractedData([]);
        setFieldMapping({});
        setConflicts([]);
        setUploadResults({ success: 0, errors: 0, conflicts: 0 });
    };

    const getSelectedEntityConfig = () => {
        return entityOptions.find(e => e.name === selectedEntity);
    };

    return (
        <Card className="border-0 shadow-md">
            <CardHeader>
                <CardTitle className="flex items-center gap-2">
                    <Upload className="w-5 h-5 text-[#00C2C2]" />
                    CSV Data Upload
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
                {uploadStep === 'select' && (
                    <div className="space-y-4">
                        <div>
                            <label className="text-sm font-medium mb-2 block">Select Entity Type</label>
                            <Select value={selectedEntity} onValueChange={handleEntitySelect}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Choose which entity to upload data to..." />
                                </SelectTrigger>
                                <SelectContent>
                                    {entityOptions.map(option => (
                                        <SelectItem key={option.name} value={option.name}>
                                            {option.label} ({option.name})
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                )}

                {uploadStep === 'upload' && (
                    <div className="space-y-4">
                        <Alert>
                            <FileText className="h-4 w-4" />
                            <AlertDescription>
                                Uploading to: <strong>{getSelectedEntityConfig()?.label}</strong>
                                <br />
                                Please ensure your CSV has column headers that match the entity field names.
                            </AlertDescription>
                        </Alert>
                        
                        <div>
                            <label className="text-sm font-medium mb-2 block">Select CSV File</label>
                            <Input
                                type="file"
                                accept=".csv"
                                onChange={handleFileSelect}
                                className="cursor-pointer"
                            />
                        </div>
                        
                        {file && (
                            <div className="flex items-center gap-3">
                                <FileText className="w-4 h-4 text-[#00C2C2]" />
                                <span className="text-sm">{file.name}</span>
                                <Badge variant="outline">CSV</Badge>
                            </div>
                        )}
                        
                        <div className="flex gap-3">
                            <Button onClick={handleFileUpload} disabled={!file || isLoading}>
                                {isLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                                Process File
                            </Button>
                            <Button variant="outline" onClick={resetUploader}>
                                Back
                            </Button>
                        </div>
                    </div>
                )}

                {uploadStep === 'preview' && (
                    <div className="space-y-4">
                        <Alert>
                            <CheckCircle className="h-4 w-4" />
                            <AlertDescription>
                                Found <strong>{extractedData.length}</strong> records in your CSV file.
                                {conflicts.length > 0 && (
                                    <span className="block mt-1">
                                        <AlertTriangle className="inline w-3 h-3 mr-1 text-yellow-500" />
                                        {conflicts.length} potential conflicts found (will be updated)
                                    </span>
                                )}
                            </AlertDescription>
                        </Alert>

                        <div className="bg-gray-50 rounded-lg p-4">
                            <h4 className="font-medium mb-2">Field Mapping Preview</h4>
                            <div className="grid grid-cols-2 gap-2 text-sm">
                                {Object.entries(fieldMapping).slice(0, 5).map(([csvField, entityField]) => (
                                    <div key={csvField} className="flex justify-between">
                                        <span className="text-gray-600">{csvField}</span>
                                        <span className="font-medium">→ {entityField}</span>
                                    </div>
                                ))}
                                {Object.keys(fieldMapping).length > 5 && (
                                    <div className="col-span-2 text-gray-500">
                                        ...and {Object.keys(fieldMapping).length - 5} more fields
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="flex gap-3">
                            <Button onClick={handleDataUpload} disabled={isLoading}>
                                {isLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                                Upload {extractedData.length} Records
                            </Button>
                            <Button variant="outline" onClick={() => setUploadStep('upload')}>
                                Back
                            </Button>
                        </div>
                    </div>
                )}

                {uploadStep === 'processing' && (
                    <div className="text-center py-8">
                        <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-[#00C2C2]" />
                        <h3 className="font-medium">Uploading data...</h3>
                        <p className="text-sm text-gray-500">This may take a moment for large files.</p>
                    </div>
                )}

                {uploadStep === 'complete' && (
                    <div className="space-y-4">
                        <Alert>
                            <CheckCircle className="h-4 w-4" />
                            <AlertDescription>
                                <strong>Upload completed successfully!</strong>
                            </AlertDescription>
                        </Alert>

                        <div className="grid grid-cols-3 gap-4 text-center">
                            <div className="bg-green-50 rounded-lg p-3">
                                <div className="text-2xl font-bold text-green-600">{uploadResults.success}</div>
                                <div className="text-sm text-green-700">New Records</div>
                            </div>
                            <div className="bg-blue-50 rounded-lg p-3">
                                <div className="text-2xl font-bold text-blue-600">{uploadResults.conflicts}</div>
                                <div className="text-sm text-blue-700">Updated</div>
                            </div>
                            <div className="bg-red-50 rounded-lg p-3">
                                <div className="text-2xl font-bold text-red-600">{uploadResults.errors}</div>
                                <div className="text-sm text-red-700">Errors</div>
                            </div>
                        </div>

                        <Button onClick={resetUploader} className="w-full">
                            <RefreshCw className="w-4 h-4 mr-2" />
                            Upload More Data
                        </Button>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}